import { sv, VNode } from "@dhx/ts-common/dom";
import { IRadarScaleDrawData  } from "../types";
import { verticalBottomText, verticalCenteredText, verticalTopText } from "./common";

export function getCoordinates(percent: number, radiusX: number, radiusY: number): [number, number] {
	const x = Math.cos(2 * Math.PI * percent) * radiusX;
	const y = Math.sin(2 * Math.PI * percent) * radiusY;
	return [x, y];
}
export function shiftCoordinates(item: [number, number], dx: number, dy: number): [number, number] {
	return [item[0] + dx, item[1] + dy];
}

export const pieLikeHandlers = {
	onmouseover(shiftX: number, shiftY: number, _, node: VNode) {
		node.el.setAttribute("transform", `translate(${shiftX}, ${shiftY}) scale(1.05)`);
		node.el.classList.add("dhx_pie-transform-delay");
	},
	onmouseout(_, node: VNode) {
		node.el.setAttribute("transform", `translate(0, 0)`);
		node.el.classList.remove("dhx_pie-transform-delay");
	}
};

function checkMiss(v: number, r: number): boolean {
	const miss = .000001;
	return v - miss < r && v + miss > r;
}
function drawBackgroundCircle(radius: number, color: string): VNode {
	return sv("circle", {cx: 0, cy: 0, r: radius, fill: color, stroke: "none", class: "background-circle" });
}
function arc(r: number, flag: boolean): string {
	return `M${-r},0A${r},${r} 0 ${flag ? 0 : 1} 1 ${r},0A${r},${r} 0 ${flag ? 0 : 1} 1 ${-r},0`;
}

export function radarScale(data: IRadarScaleDrawData, width: number, height: number): VNode {
	const radius = height / 2;
	const scalePercent = 1 / data.scales.length;
	const largeArcFlag = scalePercent > .5 ? 1 : 0;
	const svg = [];
	const background = drawBackgroundCircle(radius, "#FAFBFD");
	svg.push(background);
	let currentPercent = -.25;
	const grid = [];
	const axis = data.axis;
	const gridClass = `radar-grid ${data.zebra ? "zebra" : ""}`;
	for (let i=1; i<axis.length; i+=2) {
		const r1 = radius * axis[i - 1];
		const r2 = radius * axis[i];
		const d = `${arc(r1, true)} ${arc(r2, false)}`;
		const arcs = sv("path", {d, fill: "none", stroke: "black", class: gridClass});
		grid.push(arcs);
	}
	svg.push(grid);
	data.scales.forEach(item => {
		const [startX, startY] = getCoordinates(currentPercent, radius, radius);
		const nextPercent = currentPercent + scalePercent;
		const [endX, endY] = getCoordinates(nextPercent, radius, radius);
		const d = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;
		const path = sv("path", {
			d,
			stroke: "black",
			fill: "none",
			class: "radar-scale"
		});
		svg.push(path);

		const [yTextPadding, xTextPadding] = [8, 8];
		const dy = checkMiss(currentPercent, 0) || checkMiss(currentPercent, 0.5) ? 0 : currentPercent < 0 || currentPercent > 0.5 ? -yTextPadding : yTextPadding;
		const dx = checkMiss(currentPercent, -0.25) || checkMiss(currentPercent, 0.25) ? 0 : currentPercent < -0.25 || currentPercent > 0.25 ? -xTextPadding : xTextPadding;

		if (checkMiss(currentPercent, -0.25) || checkMiss(currentPercent, 0.25)) {
			const alignFn = checkMiss(currentPercent, -0.25) ? verticalTopText : verticalBottomText;
			const text = sv("text", {x: startX + dx, y: startY + dy, class: "scale-text" }, [alignFn(item)]);
			svg.push(text);
		} else {
			const className =  currentPercent >= -0.25 && currentPercent <= 0.25 ? "start-text scale-text" : "end-text scale-text";
			const text = sv("text", {x: startX + dx, y: startY + dy, class: className }, [verticalCenteredText(item)]);
			svg.push(text);
		}

		currentPercent = nextPercent;
	});

	currentPercent = -.25;

	if (data.realAxis) {
		const scaleText = data.realAxis.map((item, index) => {
			const [x, y] = getCoordinates(-0.25, radius * axis[index], radius * axis[index]);
			return sv("text", {x, y, dx: -10, class: "radar-axis-text"}, [verticalCenteredText(item.toString())]);
		});
		svg.push(scaleText);
	}
	return sv("g", { transform: `translate(${width / 2}, ${height / 2})` }, svg);
}